// src/store/api/bannersApi.ts - Simplified version
import type {BannerResponseDto} from "../../types/banner";
import {baseApi} from "./baseApi";

export const bannerApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Banner - Fixed FormData implementation
    createBanner: builder.mutation<
      BannerResponseDto,
      {
        file: File;
        title?: string;
        description?: string;
      }
    >({
      query: (data) => {
        const formData = new FormData();

        // Add required image file
        formData.append("image", data.file, data.file.name);

        // Add optional fields if provided
        if (data.title?.trim()) {
          formData.append("title", data.title.trim());
        }

        if (data.description?.trim()) {
          formData.append("description", data.description.trim());
        }

        return {
          url: "/banners",
          method: "POST",
          body: formData,
          // RTK Query will automatically detect FormData and set proper headers
        };
      },
      invalidatesTags: ["Banner"],
    }),

    // Get All Banners - Simple list without pagination
    getBanners: builder.query<BannerResponseDto[], void>({
      query: () => "/banners",
      transformResponse: (response: {data: BannerResponseDto[]}) => response.data,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({id}) => ({type: "Banner" as const, id})),
              {type: "Banner", id: "LIST"},
            ]
          : [{type: "Banner", id: "LIST"}],
    }),

    // Get Banner by ID
    getBannerById: builder.query<BannerResponseDto, number>({
      query: (id) => `/banners/${id}`,
      transformResponse: (response: {data: BannerResponseDto}) => response.data,
      providesTags: (result, error, id) => [{type: "Banner", id}],
    }),

    // Update Banner - Simple text fields only (no image update)
    updateBanner: builder.mutation<
      BannerResponseDto,
      {
        id: number;
        title?: string;
        description?: string;
      }
    >({
      query: ({id, ...data}) => {
        console.log("🔄 API updateBanner called with:", {id, data});
        return {
          url: `/banners/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (result, error, {id}) => {
        console.log("🏷️ Invalidating tags for banner:", id);
        return [
          {type: "Banner", id},
          {type: "Banner", id: "LIST"},
        ];
      },
    }),

    // ✅ NEW: Update Banner with Image
    updateBannerWithImage: builder.mutation<
      BannerResponseDto,
      {
        id: number;
        file: File;
        title?: string;
        description?: string;
      }
    >({
      query: ({id, file, title, description}) => {
        const formData = new FormData();

        // Add required image file
        formData.append("image", file, file.name);

        // Add optional fields if provided
        if (title?.trim()) {
          formData.append("title", title.trim());
        }

        if (description?.trim()) {
          formData.append("description", description.trim());
        }

        console.log("🔄 API updateBannerWithImage called for banner:", id);

        return {
          url: `/banners/${id}/image`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: (result, error, {id}) => [
        {type: "Banner", id},
        {type: "Banner", id: "LIST"},
      ],
    }),

    // Delete Banner
    deleteBanner: builder.mutation<{success: boolean; message: string}, number>(
      {
        query: (id) => ({
          url: `/banners/${id}`,
          method: "DELETE",
        }),
        invalidatesTags: (result, error, id) => [
          {type: "Banner", id},
          {type: "Banner", id: "LIST"},
        ],
      }
    ),
  }),
  overrideExisting: false,
});

export const {
  useCreateBannerMutation,
  useGetBannersQuery,
  useGetBannerByIdQuery,
  useUpdateBannerMutation,
  useUpdateBannerWithImageMutation, // ✅ Export new hook
  useDeleteBannerMutation,
} = bannerApi;
